import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Menu, MenuItem, Snackbar, Alert,
} from '@mui/material';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { ButtonOutlined } from '../../UI';
import { buscarFasesAndInfoAtuaisProcessos } from '../../../../services/core/processos/processosService';
import DialogEnviarOutraFase from './DialogEnviarOutraFase';
import DialogEnviarEmailDevedor from './DialogEnviarEmailDevedor';

/**
 * Menu "AÇÕES EM MASSA" de `GridProcessos.jsx` — porta as regras de
 * habilitação de `AcoesPesquisaProcesso.jsx` (tela real) literalmente, ver
 * `docs/architecture/plano-acoes-em-massa-grid-processos.md` (Fluxo 0).
 * Etapa 4 (final) do plano: os 2 dialogs (Enviar Processos para outra Fase /
 * Enviar E-mail de Notificação de Cobrança) estão funcionais.
 *
 * `feedback`/o `Snackbar` abaixo vivem aqui (não dentro de cada dialog)
 * porque os dois dialogs fecham (desmontam, `Dialog` não usa `keepMounted`)
 * assim que o envio tem sucesso — um Snackbar renderizado só dentro deles
 * desapareceria antes do usuário ler a mensagem.
 */
export default function AcoesEmMassaMenu({ idsProcessos, totalSelecionados }) {
  const [anchor, setAnchor] = useState(null);
  const [fasesAndInfoProcessosSelecionados, setFasesAndInfoProcessosSelecionados] = useState([]);
  const [openEnviarOutraFase, setOpenEnviarOutraFase] = useState(false);
  const [openEnviarEmailDevedor, setOpenEnviarEmailDevedor] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const fasesPermitidasUsuario = useSelector(state => state.authentication.userInformation.fases);
  const permissoesUsuario = useSelector(state => state.authentication.userInformation.permissoes);

  // fasesAndInfoProcessosSelecionados fica vazio antes da resposta chegar e
  // também é resetado para [] em caso de falha da requisição — `.every()`
  // sobre array vazio é `true` por vacuidade, o que sem essa guarda
  // habilitaria as opções exatamente quando não há dado nenhum sobre
  // fase/estágio dos processos selecionados (fail-open). Corrigido para
  // fail-closed: sem dado carregado, as 2 opções ficam desabilitadas.
  const semDadosDeFaseCarregados = fasesAndInfoProcessosSelecionados.length === 0;

  const elementsEqualFase = useMemo(
    () => (new Set(fasesAndInfoProcessosSelecionados.map(fase => fase.faseAtual))).size === 1,
    [fasesAndInfoProcessosSelecionados],
  );

  const disableButtonEnviarProcessosOutraFase = useMemo(
    () => semDadosDeFaseCarregados
      || !fasesAndInfoProcessosSelecionados.every(fase => fase.estagioFase !== 'U'
        && fasesPermitidasUsuario.some(faseUser => fase.faseAtual === parseInt(faseUser, 10))),
    [semDadosDeFaseCarregados, fasesPermitidasUsuario, fasesAndInfoProcessosSelecionados],
  );

  const disableButtonEnviarEmailDevedores = useMemo(
    () => (!permissoesUsuario.includes('ROLE_1210')
      && (semDadosDeFaseCarregados
        || !elementsEqualFase
        || !fasesAndInfoProcessosSelecionados.every(fase => fase.estagioFase !== 'U'
          && fasesPermitidasUsuario.some(faseUser => fase.faseAtual === parseInt(faseUser, 10))))),
    [semDadosDeFaseCarregados, fasesPermitidasUsuario, fasesAndInfoProcessosSelecionados, elementsEqualFase,
      permissoesUsuario],
  );

  const processosPermitemFaseCobrancaPresencial = useMemo(
    () => fasesAndInfoProcessosSelecionados.every(processo => processo.permitirCobrancaPresencial === 'S'),
    [fasesAndInfoProcessosSelecionados],
  );

  const processosTemAgendamento = useMemo(
    () => fasesAndInfoProcessosSelecionados.some(processo => processo.temAgendamento === 'S'),
    [fasesAndInfoProcessosSelecionados],
  );

  const processosTemRequisicao = useMemo(
    () => fasesAndInfoProcessosSelecionados.some(processo => processo.temRequisicao === 'S'),
    [fasesAndInfoProcessosSelecionados],
  );

  const faseProcessos = elementsEqualFase && fasesAndInfoProcessosSelecionados.length
    ? fasesAndInfoProcessosSelecionados[0].faseAtual
    : null;

  function abrirMenu(event) {
    // idsProcessos vem exclusivamente do estado de checkbox da própria grid
    // (marcados) — nunca de input livre do usuário (ver Análise de
    // Segurança do plano).
    buscarFasesAndInfoAtuaisProcessos(idsProcessos)
      .then(response => setFasesAndInfoProcessosSelecionados(response.data))
      .catch(() => setFasesAndInfoProcessosSelecionados([]));
    setAnchor(event.currentTarget);
  }

  function fecharMenu() {
    setAnchor(null);
  }

  function abrirEnviarOutraFase() {
    fecharMenu();
    setOpenEnviarOutraFase(true);
  }

  function abrirEnviarEmailDevedor() {
    fecharMenu();
    setOpenEnviarEmailDevedor(true);
  }

  // Referência estável: os dialogs filhos usam esta callback como dependência
  // de `useCallback`/`useEffect` internos (ex.: `limparFormulario` em
  // `DialogEnviarEmailDevedor.jsx`) — uma arrow function nova a cada render
  // do menu recriaria essas dependências e reexecutaria efeitos de "abrir o
  // dialog" mesmo com ele já aberto, apagando o formulário em digitação
  // (achado em code review).
  const onFeedback = useCallback((severity, mensagem) => setFeedback({ severity, mensagem }), []);
  const onSucesso = useCallback(mensagem => setFeedback({ severity: 'success', mensagem }), []);

  return (
    <>
      <ButtonOutlined
        size="small"
        startIcon={<PlaylistAddCheckIcon />}
        disabled={totalSelecionados < 2}
        onClick={abrirMenu}
      >
        AÇÕES EM MASSA
        {totalSelecionados ? ` (${totalSelecionados})` : ''}
      </ButtonOutlined>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={fecharMenu}>
        <MenuItem disabled={disableButtonEnviarProcessosOutraFase} onClick={abrirEnviarOutraFase}>
          Enviar Processos para outra Fase
        </MenuItem>
        <MenuItem disabled={disableButtonEnviarEmailDevedores} onClick={abrirEnviarEmailDevedor}>
          Enviar E-mail de Notificação de Cobrança
        </MenuItem>
      </Menu>

      <DialogEnviarOutraFase
        open={openEnviarOutraFase}
        onClose={() => setOpenEnviarOutraFase(false)}
        onSucesso={onSucesso}
        processosSelecionados={idsProcessos}
        elementsEqualFase={elementsEqualFase}
        faseProcessos={faseProcessos}
        processosPermitemFaseCobrancaPresencial={processosPermitemFaseCobrancaPresencial}
        processosTemAgendamento={processosTemAgendamento}
        processosTemRequisicao={processosTemRequisicao}
      />

      <DialogEnviarEmailDevedor
        open={openEnviarEmailDevedor}
        onClose={() => setOpenEnviarEmailDevedor(false)}
        onFeedback={onFeedback}
        processosSelecionados={idsProcessos}
      />

      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={3000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={feedback?.severity || 'success'} variant="filled" onClose={() => setFeedback(null)}>
          {feedback?.mensagem || ''}
        </Alert>
      </Snackbar>
    </>
  );
}

AcoesEmMassaMenu.propTypes = {
  idsProcessos: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])).isRequired,
  totalSelecionados: PropTypes.number.isRequired,
};
